import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DollarSign, Users, ClipboardCheck, TrendingUp, Download, FileSpreadsheet, Star,
} from 'lucide-react';
import { toast } from 'sonner';

const TallerReportsTab = ({ API, sessionToken }) => {
  const [summary, setSummary] = useState(null);
  const [surveyStats, setSurveyStats] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${sessionToken}` };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo).toISOString();
      const [sumRes, surveyRes] = await Promise.all([
        axios.get(`${API}/taller/reports/summary`, { headers, params }),
        axios.get(`${API}/taller/surveys/stats`, { headers }),
      ]);
      setSummary(sumRes.data);
      setSurveyStats(surveyRes.data);
    } catch (e) {
      toast.error('Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = async (kind) => {
    try {
      const params = { format: 'csv' };
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo).toISOString();
      const res = await axios.get(`${API}/taller/reports/${kind}`, {
        headers,
        params,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `reporte_${kind}_${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Reporte de ${kind} descargado`);
    } catch (e) {
      toast.error('Error al descargar reporte');
    }
  };

  if (loading) return <div className="text-sm text-slate-500 p-6">Cargando reportes…</div>;
  if (!summary) return <div className="text-sm text-slate-500 p-6">Sin datos.</div>;

  return (
    <div className="space-y-6" data-testid="taller-reports-tab">
      <Card className="p-5">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Filtros</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="from">Desde</Label>
            <Input id="from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="report-date-from" />
          </div>
          <div>
            <Label htmlFor="to">Hasta</Label>
            <Input id="to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="report-date-to" />
          </div>
          <Button onClick={fetchAll} className="bg-purple-600 hover:bg-purple-700" data-testid="apply-report-filters-btn">
            Aplicar
          </Button>
          <Button variant="outline" onClick={() => { setDateFrom(''); setDateTo(''); setTimeout(fetchAll, 0); }}>
            Limpiar
          </Button>
        </div>
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="report-kpi-revenue" label="Ingreso Bruto" value={`₡${summary.gross_revenue.toLocaleString()}`} icon={<DollarSign className="w-5 h-5" />} color="from-emerald-500 to-emerald-600" sub={`Neto: ₡${summary.net_revenue.toLocaleString()}`} />
        <KpiCard testid="report-kpi-ticket" label="Ticket promedio" value={`₡${summary.avg_ticket.toLocaleString()}`} icon={<TrendingUp className="w-5 h-5" />} color="from-purple-500 to-purple-600" />
        <KpiCard testid="report-kpi-appointments" label="Citas totales" value={summary.total_appointments} icon={<ClipboardCheck className="w-5 h-5" />} color="from-blue-500 to-blue-600" sub={`${summary.completed_appointments} completadas`} />
        <KpiCard testid="report-kpi-clients" label="Clientes únicos" value={summary.unique_clients} icon={<Users className="w-5 h-5" />} color="from-amber-500 to-amber-600" sub={`Conversión bids: ${summary.conversion_rate}%`} />
      </div>

      {surveyStats && surveyStats.total > 0 && (
        <Card className="p-5">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            Satisfacción de clientes ({surveyStats.total} encuestas)
          </h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <MiniKpi label="NPS" value={surveyStats.nps} sub={`${surveyStats.nps_promoters} promotores · ${surveyStats.nps_detractors} detractores`} color="bg-purple-50" />
            <MiniKpi label="CSAT" value={`${surveyStats.csat} / 5`} sub="Satisfacción promedio" color="bg-emerald-50" />
            <MiniKpi label="CES" value={`${surveyStats.ces} / 7`} sub="Facilidad de resolución" color="bg-blue-50" />
          </div>
        </Card>
      )}

      <Card className="p-5">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Exportar reportes (CSV)</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <Button onClick={() => downloadCSV('services')} variant="outline" className="justify-start h-auto py-4" data-testid="download-services-csv-btn">
            <FileSpreadsheet className="w-5 h-5 mr-3 text-emerald-600" />
            <div className="text-left">
              <div className="font-medium">Servicios</div>
              <div className="text-xs text-slate-500">Citas + detalles + montos</div>
            </div>
            <Download className="w-4 h-4 ml-auto" />
          </Button>
          <Button onClick={() => downloadCSV('parts')} variant="outline" className="justify-start h-auto py-4" data-testid="download-parts-csv-btn">
            <FileSpreadsheet className="w-5 h-5 mr-3 text-indigo-600" />
            <div className="text-left">
              <div className="font-medium">Repuestos</div>
              <div className="text-xs text-slate-500">Por cita + estado</div>
            </div>
            <Download className="w-4 h-4 ml-auto" />
          </Button>
          <Button onClick={() => downloadCSV('clients')} variant="outline" className="justify-start h-auto py-4" data-testid="download-clients-csv-btn">
            <FileSpreadsheet className="w-5 h-5 mr-3 text-amber-600" />
            <div className="text-left">
              <div className="font-medium">Clientes</div>
              <div className="text-xs text-slate-500">Atendidos + monto total</div>
            </div>
            <Download className="w-4 h-4 ml-auto" />
          </Button>
        </div>
      </Card>
    </div>
  );
};

const KpiCard = ({ icon, label, value, color, sub, testid }) => (
  <Card className="p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm text-slate-500">{label}</span>
      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${color} text-white flex items-center justify-center`}>
        {icon}
      </div>
    </div>
    <div className="text-2xl font-bold text-slate-900">{value}</div>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </Card>
);

const MiniKpi = ({ label, value, sub, color }) => (
  <div className={`${color} rounded-lg p-4`}>
    <p className="text-xs text-slate-500 uppercase">{label}</p>
    <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </div>
);

export default TallerReportsTab;
